import React, { PropsWithChildren, ReactNode, useContext, useEffect, useState } from "react";
import { ACCESS_DENIED, getPropertyRpt } from "../lib/auth";
import {
  KeycloakRPTSingleton,
  useKeycloakAuthz,
} from "./keycloak-provider";
import { defineMessages, FormattedMessage } from "react-intl";
import {
  GetPropertyInfoRequest,
  GetPropertyInfoResponse,
} from "../gen/witbooking/backoffice/property/v1/property_api_pb";
import { useGrpcClient } from "../lib/grpc";
import { PropertyAPIPromiseClient } from "../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import { PropertyLocalesProvider } from "./property-locale-provider";
import { KeycloakAuthorizationInstance } from "keycloak-js/dist/keycloak-authz";

export interface PropertyProviderProps {
  propertyTicker: string;
  loader: ReactNode;
}

export interface IProperty {
  ticker: string;
  token: string;
  info: GetPropertyInfoResponse.AsObject;
}

const messages = defineMessages({
  propertyAccessDenied: {
    id: "global.propertyAccessDenied",
    defaultMessage: "You do not have access to this property.",
  },
});

const PropertyContext = React.createContext<IProperty | undefined>(undefined);

export async function getMultiplePropertyRPT(
  propertyTickerList: string[],
  keycloakAuthz: KeycloakAuthorizationInstance
) {
  try {
    const rptPromises = propertyTickerList.map((property: string) => {
      return getPropertyRpt(property, keycloakAuthz);
    });

    const rptList = await Promise.all(rptPromises);

    if (Array.isArray(rptList)) {
      return rptList.map((token, index) => ({ propertyTicker: propertyTickerList[index], token }));
    }
  } catch (e) {
    if (e === ACCESS_DENIED) {
      return;
    } else {
      console.error("Error retrieving rpt", e);
      return;
    }
  }
}

export function PropertyProvider({
  propertyTicker,
  loader,
  children,
}: PropsWithChildren<PropertyProviderProps>) {
  const [value, setValue] = useState<IProperty | undefined>();
  const [status, setStatus] = useState<"loading" | "ok" | "denied" | "error">("loading");
  const keycloakAuthz = useKeycloakAuthz();
  const grpcClient = useGrpcClient(PropertyAPIPromiseClient);
  useEffect(() => {
    async function exec() {
      try {
        setStatus("loading");
        const token = await getPropertyRpt(propertyTicker, keycloakAuthz);
        KeycloakRPTSingleton.rpt = token;
        const propertyInfo = await grpcClient.getPropertyInfo(new GetPropertyInfoRequest(), {
          Authorization: `Bearer ${token}`,
        });
        setValue({
          ticker: propertyTicker,
          token: token,
          info: propertyInfo.toObject(),
        });
        setStatus("ok");
      } catch (e) {
        if (e === ACCESS_DENIED) {
          setStatus("denied");
        } else {
          console.error("Error retrieving rpt", e);
          setStatus("error");
        }
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    exec();
  }, [keycloakAuthz, propertyTicker, grpcClient]);

  return (
    <>
      {status === "loading" && loader}
      {status === "ok" && (
        <PropertyContext.Provider value={value}>
          <PropertyLocalesProvider locales={value?.info.localesList || []}>
            {children}
          </PropertyLocalesProvider>
        </PropertyContext.Provider>
      )}
      {status === "denied" && <FormattedMessage {...messages.propertyAccessDenied} />}
    </>
  );
}

export function useProperty(): IProperty {
  const property = useContext(PropertyContext);
  if (property == null) {
    throw new Error("You must call useProperty only when inside a PropertyProvider.");
  }
  return property;
}

export function usePropertyOptional(): IProperty | undefined {
  return useContext(PropertyContext);
}

//This one is for compatibility with witadmin
export function withProperty<P = {}>(
  Component: React.ComponentType<P & { property: IProperty }>
): React.ComponentType<P> {
  return (props: P) => {
    const data = useProperty();
    return <Component property={data} {...props} />;
  };
}
